import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Columns, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import GridComponent from './UpdatableGrid';
import { API_URL } from './constants';
import { toast, ToastContainer } from 'react-toastify';
import DownloadDropdown from './DownloadDropdown';
import CollaborativeGrid from './CollaborativeGrid';
import { loadDatasetContent, handleDownload } from './APICalls';

const datasets = [
  {
    id: 1,
    name: 'Random Facts Dataset',
    rows: 10000,
    useCases: [
      'AI Model FineTuning',
      'Topic Classification'
    ],
    filename: 'random_facts_dataset.csv',
    domain: 'Education',
    language: 'English',
    description: 'A synthetic dataset of short, well-structured facts spanning topics like trivia, health, astronomy and so on, designed for use in NLP and educational applications.'
  },
  {
    id: 2,
    name: 'DPO Dataset',
    rows: "3000+",
    useCases: [
      'Reducing Sycophancy in LLMs',
      'Response Quality Evaluation',
      'Preference Optimization for Open-ended Tasks'
    ],
    filename: 'dpo_dataset.csv',
    domain: 'AI Model FineTuning',
    language: 'English',
    description: 'A high-quality dataset designed for Direct Preference Optimization (DPO), consisting of instructional prompts paired with preferred and rejected completions. Ideal for fine-tuning large language models to improve helpfulness, reduce bias, and align outputs with human-preferred responses across diverse tasks.'
  },
  {
    id: 3,
    name: 'Medical Article Classification Dataset',
    rows: 500,
    useCases: [
      'Medical Text Classification',
      'Specialization-Based Article Tagging',
      'Educational NLP Demos in Healthcare'
    ],
    filename: 'data_medical_article_classification.csv',
    domain: 'Education',
    language: 'English',
    description: 'A synthetic dataset containing 500 medical article titles, each labeled with a medical specialization such as Cardiology, Neurology, or Oncology. Generated for safe and reproducible experimentation, this dataset is ideal for training classification models, building prompt templates, and educational use in healthcare-related NLP.'
  },
  {
    id: 4,
    name: "Biology QA Dataset – Conceptual Questions with Short and Long Answers",
    rows: "600+",
    useCases: [
      "Instruction-Tuned QA Models",
      "Flashcard & Quiz App Development",
      "AI Evaluation Benchmark for Education"
    ],
    filename: 'final_biology_qa_dataset.csv',
    domain: 'Education',
    language: 'English',
    description: 'This dataset contains biology-themed question–answer pairs designed to support both educational applications and AI model development. Each record consists of a factual or conceptual question, a concise short answer (suitable for flashcards or chatbots), and an extended long-form answer (ideal for tutor-style explanations or generative tasks).'
  },
  {
    id: 5,
    name: "Healthcare Agentic Conversations",
    rows: "500+",
    useCases: [
      "Agentic AI for Healthcare Customer Support",
      "Fine-tuning Tool-Calling LLMs",
      "Few-shot prompts for Medical Support Agents"
    ],
    filename: 'healthcare_agentic_conversations.csv',
    domain: 'Customer Support',
    language: 'English',
    description: 'This dataset contains structured multi-turn conversations between patients and hospital agents. Designed to train LLMs with custom tool-using abilities, each entry captures realistic healthcare scenarios such as booking and rescheduling appointments, arranging transport, requesting vaccine info, and more.'
  },
  {
    id: 6,
    name: 'FAQ of an E-Commerce Application',
    rows: "300+",
    useCases: [
      'Customer Support Automation',
      'FAQ Generation',
      'Chatbot Training'
    ],
    filename: 'faq_ecommerce.csv',
    domain: 'Customer Support',
    language: 'English',
    description: 'A curated dataset of frequently asked questions and their corresponding answers from e-commerce customer support interactions. Ideal for building FAQ systems and training customer service chatbots.'
  },
  {
    id: 7,
    name: 'Socratic Teaching Dialogue Completion',
    rows: "500+",
    useCases: [
      'AI Tutoring Agents',
      'Socratic Questioning Research',
      'Education-focused LLM Fine-tuning'
    ],
    filename: 'socratic_final.csv',
    domain: 'Education',
    language: 'English',
    description: 'A curated dataset of Socratic-style student-tutor dialogues completed with pedagogically sound reasoning steps. Ideal for training AI systems to guide students toward understanding concepts gradually rather than providing direct answers. Useful for building interactive educational agents, dialog evaluation models, and structured tutoring flows.'
  },
  {
    id: 8,
    name: 'Student Percentage Prediction with Regression',
    rows: 5000,
    useCases: [
      'Educational Analytics',
      'Performance Prediction',
      'Student Success Analysis'
    ],
    filename: 'student_final_grades.csv',
    domain: 'Education',
    language: 'English',
    description: 'A comprehensive dataset of student academic records and performance metrics. This dataset can be used to learn data cleaning concepts like removing irrelevant columns, filling missing data, fitting regression models and so on.'
  },
  {
    id: 9,
    name: 'Scam Message Classification - Telugu',
    rows: 5000,
    useCases: [
      'Binary Classification for Scam Detection',
      'Benchmarking LLMs/SLMs on Regional Languages',
      'Fine-tuning Small Language Models (e.g., Gemma 1B)'
    ],
    filename: 'scam_data_telugu_balanced.csv',
    domain: 'Security',
    language: 'Telugu',
    description: 'A dataset of 5000 synthetic and curated SMS-style messages labeled as "Potential Scam" or "Not Scam". Covers common and rare scam scenarios such as fake gifts, job offers, bank fraud, digital arrests, and more. Balanced across classes, designed for scam detection research and deployment in low-resource languages.'
  }
  // {
  //   id: 10,
  //   name: 'Customer Agent Conversations',
  //   rows: 5000,
  //   useCases: [
  //     'Binary Classification for Scam Detection',
  //     'Benchmarking LLMs/SLMs on Regional Languages',
  //     'Fine-tuning Small Language Models (e.g., Gemma 1B)'
  //   ],
  //   filename: 'customer_agent_conversations_wide.csv',
  //   domain: 'Security',
  //   language: 'Telugu',
  //   description: 'A dataset of 5000 synthetic and curated SMS-style messages labeled as "Potential Scam" or "Not Scam". Covers common and rare scam scenarios such as fake gifts, job offers, bank fraud, digital arrests, and more. Balanced across classes, designed for scam detection research and deployment in low-resource languages.'
  // }
];

const domainColors = {
  'E-commerce': 'bg-blue-100 text-blue-800',
  'Customer Support': 'bg-green-100 text-green-800',
  'Security': 'bg-red-100 text-red-800',
  'Education': 'bg-purple-100 text-purple-800',
  'AI Model FineTuning': 'bg-yellow-100 text-green-800'
};

const languageColors = {
  'English': 'bg-indigo-100 text-indigo-800',
  'Telugu': 'bg-orange-100 text-orange-800'
};

const DatasetListing = ({ user }) => {
  const [selectedDomain, setSelectedDomain] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [expandedDataset, setExpandedDataset] = useState(null);
  const [gridData, setGridData] = useState(null);
  const [gridColumns, setGridColumns] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDatasetId, setCurrentDatasetId] = useState(null);
  const [improvement, setImprovement] = useState('');
  const [background, setBackground] = useState('');
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorData, setEditorData] = useState(null);
  const navigate = useNavigate();

  // Filter datasets based on both domain and language
  const filteredDatasets = datasets.filter(d => {
    const domainMatch = !selectedDomain || d.domain === selectedDomain;
    const languageMatch = !selectedLanguage || d.language === selectedLanguage;
    return domainMatch && languageMatch;
  });

  const handleLanguageClick = (language) => {
    setSelectedLanguage(prevLanguage =>
      prevLanguage === language ? '' : language
    );
  };

  const loadDataIntoGrid = async (filename) => {
    setGridData(null); // Clear existing data
    setIsDownloadDisabled(true); // Disable download while loading
    var results = await loadDatasetContent(filename, user ? user.accessToken : '');
    setGridColumns(results[0]);
    setGridData(results[1]); // Show first 100 rows for preview
    setIsDownloadDisabled(false);
  }

  const handleDatasetClick = async (dataset) => {
    // if (user === null) {
    //   setIsLoggedIn(false);
    //   navigate("/signin");
    //   return;
    // }

    if (expandedDataset === dataset.id) {
      setExpandedDataset(null);
    } else {
      setExpandedDataset(dataset.id);
      await loadDataIntoGrid(dataset.filename);
    }
  };

  const handleApplyToReview = (datasetId) => {
    setCurrentDatasetId(datasetId);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setImprovement('');
    setBackground('');
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();

    try {
      await fetch(`${API_URL}/api/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: user.uid,
          datasetId: currentDatasetId,
          improvement: improvement,
          background: background,
        })
      });
      toast.success('Thank you for applying to be a reviewer. Please click on Submit Dataset to add a reviewed version of the dataset.');
      handleModalClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Error submitting feedback');
    }
  };

  const handleDownloadClick = async (dataset) => {
    if (user === null) {
      setIsLoggedIn(false);
      navigate("/signin?returnTo=/cdatasets"); // Add return URL
      return;
    }

    if (!gridData || expandedDataset !== dataset.id) {
      setExpandedDataset(dataset.id);
      await loadDatasetContent(dataset.filename);
    }
  };

  const handleMakeChanges = async (dataset) => {
    if (user === null) {
      setIsLoggedIn(false);
      navigate("/signin");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/datasets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filename: dataset.filename
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to load dataset');
      }

      const csvText = await response.text();

      Papa.parse(csvText, {
        header: true,
        dynamicTyping: true,
        complete: (results) => {
          setEditorData(results.data);
          setIsEditorOpen(true);
        },
        error: (error) => {
          console.error('Error parsing CSV:', error);
          toast.error('Error loading dataset');
        }
      });
    } catch (error) {
      console.error('Error loading dataset:', error);
      toast.error('Error loading dataset');
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Available Datasets</h1>
          <p className="text-gray-600 mt-1">Browse and download our collection of high-quality datasets</p>
        </div>
        <button
          onClick={() => navigate("/competition")}
          className="bg-blue-600 text-white px-6 py-2.5 rounded-lg shadow-md hover:bg-blue-700 transition duration-300 flex items-center gap-2"
        >
          <span>Submit Dataset</span>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Domain Filters */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold text-gray-700 mb-3">Filter by Domain</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedDomain('')}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedDomain === ''
              ? 'bg-blue-600 text-white shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
          >
            All Domains
          </button>
          {Object.keys(domainColors).map(domain => (
            <button
              key={domain}
              onClick={() => setSelectedDomain(domain)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedDomain === domain
                ? 'bg-blue-600 text-white shadow-md'
                : domainColors[domain] + ' hover:opacity-80'
                }`}
            >
              {domain}
            </button>
          ))}
        </div>
      </div>

      {/* Dataset Cards */}
      <div className="grid grid-cols-1 gap-6">
        {filteredDatasets.map((dataset) => (
          <div
            key={dataset.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Left Section - Dataset Info */}
              <div className="flex-grow space-y-4">
                <div
                  className="cursor-pointer group"
                  onClick={() => handleDatasetClick(dataset)}
                >
                  <h2 className="text-xl font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{dataset.name}</h2>
                  <div className="mt-2 flex items-center gap-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z M8 4v4 M16 4v4 M4 11h16" />
                      </svg>
                      {dataset.rows.toLocaleString()} rows
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${domainColors[dataset.domain]}`}
                      >
                        {dataset.domain}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLanguageClick(dataset.language);
                        }}
                        className={`px-2 py-1 rounded-full text-xs font-medium transition-all hover:scale-105 hover:shadow-sm ${selectedLanguage === dataset.language
                          ? 'ring-2 ring-offset-2 ring-blue-500'
                          : languageColors[dataset.language] + ' hover:opacity-90'
                          }`}
                      >
                        {dataset.language}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Use Cases Section */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Recommended Projects:</h3>
                  <ul className="space-y-2">
                    {dataset.useCases?.map((useCase, index) => (
                      <li key={index} className="flex items-start">
                        <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-gray-600">{useCase}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Right Section - Description and Actions */}
              <div className="lg:w-1/3 border-l border-gray-200 pl-6 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Dataset Description</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {dataset.description}
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                    {gridData && (
                      <DownloadDropdown
                        isDisabled={isDownloadDisabled}
                        onDownload={(format) => {
                          if (user === null) {
                            setIsLoggedIn(false);
                            navigate("/signin?returnTo=/cdatasets");
                          }
                          else {
                            handleDownload(format, gridColumns, gridData)
                          }
                        }
                        }
                      />
                    )}

                    {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMakeChanges(dataset);
                      }}
                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors ${
                        user === null 
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                    >
                      <span>Make Changes</span>
                    </button> */}
                  </div>
                </div>
              </div>
            </div>

            {/* Dataset Preview */}
            {expandedDataset === dataset.id && (
              <div className="mt-6 border-t border-gray-100 pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Dataset Preview</h3>
                  <div className="flex items-center gap-2">
                    {gridData ? (
                      <>
                        <span className="text-sm text-gray-500">
                          Showing {gridData.length} rows
                        </span>
                        <ChevronUp className="h-5 w-5 text-gray-400" />
                      </>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        <span className="text-sm text-gray-500">Loading preview...</span>
                      </div>
                    )}
                  </div>
                </div>

                {gridData && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <GridComponent
                      rowData={gridData}
                      colDefs={gridColumns}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Collaborative Editor Modal */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setIsEditorOpen(false)}></div>
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[90vh] flex flex-col">
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="text-xl font-semibold">Edit Dataset</h2>
                <button
                  onClick={() => setIsEditorOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-grow p-4">
                <CollaborativeGrid
                  initialData={editorData}
                  onDataChange={(newData) => setEditorData(newData)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-xl p-8 w-full max-w-2xl mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Apply to Review the Dataset</h2>
              <button
                onClick={handleModalClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleFeedbackSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  What would you improve?
                  <span className="ml-1 text-gray-400" title="Let us know specific aspects of the data you think could be better.">
                    (i)
                  </span>
                </label>
                <textarea
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  value={improvement}
                  onChange={(e) => setImprovement(e.target.value)}
                  required
                  placeholder="Describe the improvements you would make to this dataset..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Background
                  <span className="ml-1 text-gray-400" title="Briefly describe your field or background to help us understand your perspective.">
                    (i)
                  </span>
                </label>
                <textarea
                  className="w-full border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  rows="4"
                  value={background}
                  onChange={(e) => setBackground(e.target.value)}
                  required
                  placeholder="Tell us about your expertise and experience..."
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleModalClose}
                  className="px-5 py-2.5 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Submit Application
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="top-center" autoClose={3000} />
    </div>
  );
};

export default DatasetListing;